import { Card, Colors, Heading, Icon, Pills, Text } from '@amelio/ui-library';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { CompetencyTranslationRow } from './CompetencyTranslationRow';

import type {
    CompetencyTranslationGroup,
    CompetencyTranslationGroupSummary,
    TranslationFieldPath,
} from '@/types/competencies/CompetencyTranslation';
import type { LocalizedText } from '@/utils/localization';

import { useGetCompetencyTranslationGroup } from '@/api/competency-services/translation';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { CompetencyStatus } from '@/constants/competencies/CompetencyStatus';
import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { useAmelioTranslations } from '@/hooks';
import { computeTranslationProgress } from '@/utils/competencies';
import { getLanguageDisplayName, getTextByLanguage, hasLocalizedText } from '@/utils/localization';

interface LazyTranslationGroupAccordionProps {
    groupSummary: CompetencyTranslationGroupSummary;
    loadedGroup: CompetencyTranslationGroup | undefined;
    sourceLanguage: string;
    targetLanguage: string;
    onChange: (path: TranslationFieldPath, value: string) => void;
    onGroupLoaded: (group: CompetencyTranslationGroup) => void;
}

export const LazyTranslationGroupAccordion = ({
    groupSummary,
    loadedGroup,
    sourceLanguage,
    targetLanguage,
    onChange,
    onGroupLoaded,
}: LazyTranslationGroupAccordionProps) => {
    const { t } = useAmelioTranslations();

    const [isOpen, setIsOpen] = useState(false);
    const [shouldFetch, setShouldFetch] = useState(false);

    const { data: fetchedGroup, isLoading } = useGetCompetencyTranslationGroup(
        shouldFetch && !loadedGroup ? groupSummary.id : null,
    );

    useEffect(() => {
        if (fetchedGroup && !loadedGroup) {
            onGroupLoaded(fetchedGroup);
        }
    }, [fetchedGroup, loadedGroup, onGroupLoaded]);

    const handleToggle = useCallback(() => {
        setIsOpen((open) => {
            const willOpen = !open;
            if (willOpen && !loadedGroup) {
                setShouldFetch(true);
            }
            return willOpen;
        });
    }, [loadedGroup]);

    const source = (text: LocalizedText) => getTextByLanguage(text, sourceLanguage);
    const target = (text: LocalizedText) => text[targetLanguage] ?? '';

    const progress = loadedGroup
        ? computeTranslationProgress([loadedGroup], targetLanguage)
        : {
              translated: groupSummary.translationProgress.translated[targetLanguage] ?? 0,
              total: groupSummary.translationProgress.total,
          };
    const isComplete = progress.total > 0 && progress.translated === progress.total;

    return (
        <Card
            borderRadius={'2xl'}
            padding={'none'}
            className={'overflow-hidden'}
        >
            <button
                type={'button'}
                onClick={handleToggle}
                className={
                    // Layout and surface from `develop`; the focus ring stays on `primary-800`,
                    // which is the shade that clears the WCAG contrast bar for a focus indicator.
                    'focus-visible:ring-primary-800 flex w-full flex-col items-center justify-between gap-2 border border-gray-100 bg-purple-50 px-6 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:flex-row'
                }
                aria-expanded={isOpen}
                data-testid={`translation-group-accordion-${groupSummary.id}`}
            >
                <div className={'flex items-center gap-3'}>
                    <Heading
                        level={5}
                        weight={'semibold'}
                        className={'text-md'}
                    >
                        {source(groupSummary.name)}
                    </Heading>

                    {groupSummary.status === CompetencyStatus.INACTIVE && (
                        <Pills size={'sm'}>
                            <Text size={'small'}>{t('common.deactivated')}</Text>
                        </Pills>
                    )}

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                    >
                        {t('competencies.competencyCount', {
                            count: groupSummary.competencyCount,
                        })}
                    </Text>
                </div>

                <div className={'flex items-center gap-3'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'whitespace-nowrap'}
                        data-testid={`translation-group-count-${groupSummary.id}`}
                    >
                        {t('competenciesTranslations.fieldsTranslated', {
                            translated: progress.translated,
                            total: progress.total,
                        })}
                    </Text>

                    <Icon
                        name={isComplete ? 'essential-tick-circle' : 'essential-danger'}
                        color={isComplete ? Colors.SUCCESS : Colors.WARNING}
                    />

                    <Icon
                        name={'arrow-arrow-down'}
                        className={`transition-transform ${isOpen ? '' : '-rotate-90'}`}
                    />
                </div>
            </button>

            {isOpen && (
                <div className={'bg-white/80 px-6 py-4'}>
                    {isLoading && !loadedGroup && (
                        <div className={'flex justify-center py-8'}>
                            <OverlayLoader isLoading={true} />
                        </div>
                    )}

                    {loadedGroup && (
                        <>
                            <div className={'border-base-100 flex gap-6 border-b pb-2'}>
                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                    className={'flex-1 uppercase'}
                                >
                                    {getLanguageDisplayName(sourceLanguage)}
                                </Text>

                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                    className={'flex-1 uppercase'}
                                >
                                    {sourceLanguage !== targetLanguage
                                        ? getLanguageDisplayName(targetLanguage)
                                        : ''}
                                </Text>
                            </div>

                            <CompetencyTranslationRow
                                variant={CompetencyTranslationFieldKind.GROUP_NAME}
                                sourceText={source(loadedGroup.name)}
                                targetValue={target(loadedGroup.name)}
                                onTargetChange={(value) =>
                                    onChange(
                                        {
                                            kind: CompetencyTranslationFieldKind.GROUP_NAME,
                                            groupId: loadedGroup.id,
                                        },
                                        value,
                                    )
                                }
                            />

                            {hasLocalizedText(loadedGroup.description) && (
                                <CompetencyTranslationRow
                                    variant={CompetencyTranslationFieldKind.GROUP_DESCRIPTION}
                                    sourceText={source(loadedGroup.description)}
                                    targetValue={target(loadedGroup.description)}
                                    onTargetChange={(value) =>
                                        onChange(
                                            {
                                                kind: CompetencyTranslationFieldKind.GROUP_DESCRIPTION,
                                                groupId: loadedGroup.id,
                                            },
                                            value,
                                        )
                                    }
                                />
                            )}

                            {loadedGroup.competencies.map((competency) => (
                                <Fragment key={competency.id}>
                                    <CompetencyTranslationRow
                                        variant={CompetencyTranslationFieldKind.COMPETENCY_NAME}
                                        sourceText={source(competency.name)}
                                        targetValue={target(competency.name)}
                                        onTargetChange={(value) =>
                                            onChange(
                                                {
                                                    kind: CompetencyTranslationFieldKind.COMPETENCY_NAME,
                                                    groupId: loadedGroup.id,
                                                    competencyId: competency.id,
                                                },
                                                value,
                                            )
                                        }
                                    />

                                    {competency.description &&
                                        hasLocalizedText(competency.description) && (
                                            <CompetencyTranslationRow
                                                variant={
                                                    CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION
                                                }
                                                sourceText={source(competency.description)}
                                                targetValue={target(competency.description)}
                                                onTargetChange={(value) =>
                                                    onChange(
                                                        {
                                                            kind: CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION,
                                                            groupId: loadedGroup.id,
                                                            competencyId: competency.id,
                                                        },
                                                        value,
                                                    )
                                                }
                                            />
                                        )}

                                    {competency.questions
                                        .filter((question) => hasLocalizedText(question.label))
                                        .map((question) => (
                                            <Fragment key={question.id}>
                                                <CompetencyTranslationRow
                                                    variant={
                                                        CompetencyTranslationFieldKind.QUESTION_LABEL
                                                    }
                                                    sourceText={source(question.label)}
                                                    targetValue={target(question.label)}
                                                    onTargetChange={(value) =>
                                                        onChange(
                                                            {
                                                                kind: CompetencyTranslationFieldKind.QUESTION_LABEL,
                                                                groupId: loadedGroup.id,
                                                                competencyId: competency.id,
                                                                questionId: question.id,
                                                            },
                                                            value,
                                                        )
                                                    }
                                                />

                                                {question.options
                                                    .filter((option) =>
                                                        hasLocalizedText(option.label),
                                                    )
                                                    .map((option) => (
                                                        <CompetencyTranslationRow
                                                            key={option.id}
                                                            variant={
                                                                CompetencyTranslationFieldKind.OPTION_LABEL
                                                            }
                                                            sourceText={source(option.label)}
                                                            targetValue={target(option.label)}
                                                            onTargetChange={(value) =>
                                                                onChange(
                                                                    {
                                                                        kind: CompetencyTranslationFieldKind.OPTION_LABEL,
                                                                        groupId: loadedGroup.id,
                                                                        competencyId: competency.id,
                                                                        questionId: question.id,
                                                                        optionId: option.id,
                                                                    },
                                                                    value,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                            </Fragment>
                                        ))}
                                </Fragment>
                            ))}
                        </>
                    )}
                </div>
            )}
        </Card>
    );
};
